import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/shared/components/atoms/Input';
import { Button } from '@/shared/components/atoms/Button';
import { AuthLayout } from '../../components/AuthLayout';
import { useLoginHook } from './useLoginHook';
import './Login.css';

const FEATURES = [
  {
    title: 'Automated Briefs',
    body: 'Real-time intelligence from LinkedIn and web buying signals compiled before you pick up.',
  },
  {
    title: 'Live Response Co-Pilot',
    body: 'Direct reframes and objection anchors generated instantly as prospects speak.',
  },
  {
    title: '10-Second CRM Sync',
    body: 'Instantly draft and publish meeting notes, takeaways, and updates straight to Salesforce or HubSpot.',
  },
];

const LoginLeftContent = () => (
  <>
    <p className="login-tagline">
      Your autonomous sales co-pilot. Built to brief you, guide you, and sync your workflow instantly.
    </p>

    <div className="login-features">
      {FEATURES.map((f) => (
        <div key={f.title} className="login-feature-item">
          <span className="login-feature-bullet" />
          <p className="login-feature-text">
            <strong>{f.title}:</strong> {f.body}
          </p>
        </div>
      ))}
    </div>
  </>
);

export function Login() {
  const { formData, showPassword, isLoading, setShowPassword, handleChange, handleSubmit } = useLoginHook();

  return (
    <AuthLayout leftContent={<LoginLeftContent />}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <h2 className="auth-heading">Sign In</h2>
        <p className="auth-subheading">Enter your credentials to access the console.</p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="auth-input-group" style={{ marginBottom: '12px' }}>
            <Input
              label="Email Address"
              leftIcon={<Mail size={13} />}
              type="email"
              name="email"
              id="login-email"
              placeholder="you@company.com"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              required
            />
            <Input
              label="Password"
              leftIcon={<Lock size={13} />}
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="login-password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              autoComplete="current-password"
              required
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="password-toggle-btn"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              }
            />
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
              <Link to="/forgot-password" style={{ fontSize: '0.75rem', color: '#00cfcf', textDecoration: 'none' }}>
                Forgot password?
              </Link>
            </div>
          </div>

          <Button type="submit" variant="secondary" fullWidth isLoading={isLoading} style={{ marginTop: '8px' }}>
            Sign In
          </Button>
        </form>

        <p className="auth-switch-link-text">
          Need a workspace?{' '}
          <Link to="/signup" className="auth-switch-link">
            Create account
          </Link>
        </p>
      </motion.div>
    </AuthLayout>
  );
}
